import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, relTime, scopeQuery, useScope, type Localized } from "../api";
import { useT } from "../i18n";
import { useMe } from "./AdminShell";
import { ConfirmDelete, Field, LocalizedField, Msg, Toggle, useFlash } from "../ui";

/** Points & rewards (FR-PT-02, 06..08): point values with church override, rewards editor, redemption queue. */

type Rule = { points?: number; dailyLimit?: number | null };
interface Reward {
  id: string;
  slug: string;
  title: Localized;
  description: Localized | null;
  threshold: number;
  kind: "physical" | "digital_book" | "leader_meet";
  leaders: string[];
  fileUrl: string | null;
  active: boolean;
}
interface Redemption {
  id: string;
  rewardId: string;
  rewardTitle: Localized;
  kind: Reward["kind"];
  userId: string;
  name: string;
  alias: string;
  phone: string | null;
  preferredLeader: string | null;
  status: "redeemable" | "requested" | "date_set" | "sent";
  emailSentAt: string | null;
  createdAt: string;
  updatedAt: string;
}
interface Data {
  actions: string[];
  defaults: Record<string, Required<Rule>>;
  global: Record<string, Rule>;
  override: Record<string, Rule> | null;
  effective: Record<string, Required<Rule>>;
  rewards: Reward[];
  redemptions: Redemption[];
}

export function AdminRewards() {
  const { t, locale } = useT();
  const me = useMe();
  const scope = useScope();
  const qc = useQueryClient();
  const sq = scopeQuery(scope, "church");
  const q = useQuery({ queryKey: ["admin", "rewards", scope.churchId], queryFn: () => api<Data>(`/admin/rewards${sq}`) });
  const invalidate = () => Promise.all([qc.invalidateQueries({ queryKey: ["admin", "rewards"] }), qc.invalidateQueries({ queryKey: ["admin", "attention"] }), qc.invalidateQueries({ queryKey: ["admin", "today"] })]);
  const [flash, run, busy] = useFlash();
  const [rules, setRules] = useState<Record<string, Rule>>({});
  const [editing, setEditing] = useState<Reward | "new" | null>(null);
  const isAdmin = Boolean(me.data?.isSuperadmin || me.data?.membership?.role === "admin");
  const isGlobal = scope.canPickScope && !scope.churchId;
  const d = q.data;
  useEffect(() => {
    if (d) setRules(isGlobal ? d.global : (d.override ?? {}));
  }, [d, isGlobal]);

  const saveConfig = () => run(() => api(`/admin/rewards/config${sq}`, { method: "PUT", json: { rules } }), t("admin.rewards.saved")).then(invalidate);
  const status = useMutation({ mutationFn: (p: { id: string; status: "date_set" | "sent" }) => api(`/admin/rewards/redemptions/${p.id}/status${sq}`, { method: "POST", json: { status: p.status } }), onSuccess: invalidate });
  const resend = useMutation({ mutationFn: (id: string) => api(`/admin/rewards/redemptions/${id}/resend${sq}`, { method: "POST" }), onSuccess: invalidate });
  const L = (v: Localized | null | undefined) => (v ? (v[locale] ?? v.de ?? Object.values(v)[0] ?? "") : "");
  const scopeLabel = isGlobal ? t("admin.rewards.global") : t("admin.rewards.override");

  return (
    <>
      <h1>{t("admin.rewards.title")}</h1>
      <p className="muted">{t("admin.rewards.body")}</p>
      <Msg msg={flash} />

      <div className="two-col wide">
        <div className="panel">
          <h2>{t("admin.rewards.points")}</h2>
          <div className="table-wrap">
            <table className="people compact">
              <thead>
                <tr>
                  <th>{t("admin.rewards.action")}</th>
                  <th>{t("admin.rewards.defaultValue")}</th>
                  <th>{t("admin.rewards.value", { scope: scopeLabel })}</th>
                  <th>{t("admin.rewards.dailyLimit")}</th>
                </tr>
              </thead>
              <tbody>
                {d?.actions.map((a) => (
                  <tr key={a}>
                    <td>{t(`admin.rewards.actions.${a}`)}</td>
                    <td className="muted">
                      {d.defaults[a]?.points ?? 0}
                      {!isGlobal && d.global[a]?.points !== undefined ? ` → ${d.global[a].points}` : ""}
                    </td>
                    <td>
                      <input type="number" min={0} max={100000} style={{ width: 90 }} placeholder={String(d.effective[a]?.points ?? "")} value={rules[a]?.points ?? ""} disabled={!isAdmin} onChange={(e) => setRules({ ...rules, [a]: { ...rules[a], points: e.target.value === "" ? undefined : Number(e.target.value) } })} />
                    </td>
                    <td>
                      <input type="number" min={1} max={100} style={{ width: 70 }} placeholder={d.effective[a]?.dailyLimit === null ? t("admin.rewards.unlimited") : String(d.effective[a]?.dailyLimit ?? "")} value={rules[a]?.dailyLimit ?? ""} disabled={!isAdmin} onChange={(e) => setRules({ ...rules, [a]: { ...rules[a], dailyLimit: e.target.value === "" ? undefined : Number(e.target.value) } })} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {isAdmin ? (
            <div className="actions">
              <button className="btn" disabled={busy} onClick={() => void saveConfig()}>
                {t("admin.rewards.saveConfig")}
              </button>
            </div>
          ) : null}
        </div>

        <div className="panel">
          <div className="toolbar">
            <h2>{t("admin.rewards.rewardsList")}</h2>
            {isAdmin ? (
              <button className="btn small" onClick={() => setEditing("new")} style={{ marginInlineStart: "auto" }}>
                {t("admin.rewards.newReward")}
              </button>
            ) : null}
          </div>
          <div className="list">
            {d?.rewards.map((r) => (
              <div key={r.id} className={`listrow ${r.active ? "" : "dim"}`}>
                <div className="grow">
                  <strong>{L(r.title)}</strong> <span className="pill">{r.threshold} P</span> <span className="pill">{t(`admin.rewards.kinds.${r.kind}`)}</span>
                  {!r.active ? <span className="pill">{t("admin.rewards.inactive")}</span> : null}
                  {r.description ? <div className="muted small">{L(r.description)}</div> : null}
                </div>
                {isAdmin ? (
                  <button className="btn small ghost" onClick={() => setEditing(r)}>
                    {t("admin.common.edit")}
                  </button>
                ) : null}
              </div>
            ))}
          </div>
          {editing ? <RewardEditor key={editing === "new" ? "new" : editing.id} reward={editing === "new" ? null : editing} onClose={() => setEditing(null)} onSaved={() => void invalidate().then(() => setEditing(null))} /> : null}
        </div>
      </div>

      <div className="panel">
        <h2>{t("admin.rewards.requests")}</h2>
        {d?.redemptions.length === 0 ? <p className="muted">{t("admin.rewards.requestsEmpty")}</p> : null}
        <div className="list">
          {d?.redemptions.map((r) => (
            <div key={r.id} className="listrow">
              <div className="grow">
                <strong>{r.name}</strong> · {L(r.rewardTitle)} <span className={`pill ${r.status === "requested" ? "warn" : r.status === "sent" ? "ok" : ""}`}>{t(`admin.rewards.status.${r.status}`)}</span>
                <div className="muted small">
                  {relTime(r.updatedAt, locale)}
                  {r.preferredLeader ? ` · ${t("admin.rewards.prefers", { leader: r.preferredLeader })}` : ""}
                  {r.kind !== "digital_book" ? ` · ${r.phone ?? t("admin.rewards.noPhone")}` : ""}
                </div>
              </div>
              {r.phone && r.kind !== "digital_book" ? (
                <a className="btn small ghost" href={`tel:${r.phone}`}>
                  {t("admin.rewards.call")}
                </a>
              ) : null}
              {r.kind === "leader_meet" && r.status === "requested" ? (
                <button className="btn small" onClick={() => status.mutate({ id: r.id, status: "date_set" })}>
                  {t("admin.rewards.dateSet")}
                </button>
              ) : null}
              {r.kind !== "digital_book" && (r.status === "requested" || r.status === "date_set") ? (
                <button className="btn small ghost" onClick={() => status.mutate({ id: r.id, status: "sent" })}>
                  {t("admin.rewards.markSent")}
                </button>
              ) : null}
              {r.kind === "digital_book" ? (
                <button className="btn small ghost" onClick={() => resend.mutate(r.id)}>
                  {t("admin.rewards.resend")}
                </button>
              ) : null}
            </div>
          ))}
        </div>
      </div>
    </>
  );
}

function RewardEditor({ reward, onClose, onSaved }: { reward: Reward | null; onClose: () => void; onSaved: () => void }) {
  const { t } = useT();
  const scope = useScope();
  const sq = scopeQuery(scope, "church");
  const [flash, run, busy] = useFlash();
  const [f, setF] = useState({ title: reward?.title ?? ({} as Localized), description: reward?.description ?? ({} as Localized), threshold: String(reward?.threshold ?? 100), kind: reward?.kind ?? "physical", leaders: (reward?.leaders ?? []).join("\n"), fileUrl: reward?.fileUrl ?? "", active: reward?.active ?? true });
  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    const body = { title: f.title, description: Object.values(f.description).some(Boolean) ? f.description : undefined, threshold: Number(f.threshold), kind: f.kind, leaders: f.leaders.split("\n").map((s) => s.trim()).filter(Boolean), fileUrl: f.fileUrl.trim() || null, active: f.active };
    const ok = await run(() => (reward ? api(`/admin/rewards/${reward.id}${sq}`, { method: "PATCH", json: body }) : api(`/admin/rewards${sq}`, { method: "POST", json: body })), t("admin.common.saved"));
    if (ok) onSaved();
  };
  const remove = async () => {
    if (reward && (await run(() => api(`/admin/rewards/${reward.id}${sq}`, { method: "DELETE" })))) onSaved();
  };
  return (
    <form className="form inner" onSubmit={save}>
      <h3>{reward ? t("admin.rewards.editReward") : t("admin.rewards.newReward")}</h3>
      <LocalizedField label={t("admin.notifications.titleLabel")} value={f.title} onChange={(v) => setF({ ...f, title: v })} required />
      <LocalizedField label={t("admin.content.description")} value={f.description} onChange={(v) => setF({ ...f, description: v })} multiline />
      <div className="two-col">
        <Field label={t("admin.rewards.threshold")}>
          <input type="number" min={1} required value={f.threshold} onChange={(e) => setF({ ...f, threshold: e.target.value })} />
        </Field>
        <Field label={t("admin.rewards.kind")}>
          <select value={f.kind} onChange={(e) => setF({ ...f, kind: e.target.value as Reward["kind"] })}>
            {(["physical", "digital_book", "leader_meet"] as const).map((k) => (
              <option key={k} value={k}>
                {t(`admin.rewards.kinds.${k}`)}
              </option>
            ))}
          </select>
        </Field>
      </div>
      {f.kind === "leader_meet" ? (
        <Field label={t("admin.rewards.leaders")}>
          <textarea rows={3} value={f.leaders} onChange={(e) => setF({ ...f, leaders: e.target.value })} />
        </Field>
      ) : null}
      {f.kind === "digital_book" ? (
        <Field label={t("admin.rewards.fileUrl")}>
          <input type="url" value={f.fileUrl} onChange={(e) => setF({ ...f, fileUrl: e.target.value })} />
        </Field>
      ) : null}
      <Toggle label={t("admin.rewards.active")} checked={f.active} onChange={(v) => setF({ ...f, active: v })} />
      <div className="actions">
        <button className="btn" disabled={busy}>
          {t("admin.common.save")}
        </button>
        <button type="button" className="btn ghost" onClick={onClose}>
          {t("admin.common.cancel")}
        </button>
        <Msg msg={flash} />
        {reward ? (
          <span style={{ marginInlineStart: "auto" }}>
            <ConfirmDelete onConfirm={() => void remove()} />
          </span>
        ) : null}
      </div>
    </form>
  );
}
